/**
 * Copyright 2026 Google LLC.
 * Copyright (c) Microsoft Corporation.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * THIS FILE IS AUTOGENERATED by cddlconv 0.2.0.
 * Run `tools/update_bidi_types.py` to regenerate.
 * @see https://github.com/w3c/webdriver-bidi/blob/master/index.bs
 */

import * as z from 'zod';

export namespace Bluetooth {
  export const BluetoothUuidSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Bluetooth {
  export const BluetoothManufacturerDataSchema: z.ZodLazy<
    z.ZodObject<{
      key: z.ZodNumber;
      data: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      key: z.number().int().nonnegative(),
      data: z.string(),
    }),
  );
}
export namespace Bluetooth {
  export const CharacteristicPropertiesSchema: z.ZodLazy<
    z.ZodObject<{
      broadcast: z.ZodOptional<z.ZodBoolean>;
      read: z.ZodOptional<z.ZodBoolean>;
      writeWithoutResponse: z.ZodOptional<z.ZodBoolean>;
      write: z.ZodOptional<z.ZodBoolean>;
      notify: z.ZodOptional<z.ZodBoolean>;
      indicate: z.ZodOptional<z.ZodBoolean>;
      authenticatedSignedWrites: z.ZodOptional<z.ZodBoolean>;
      extendedProperties: z.ZodOptional<z.ZodBoolean>;
    }>
  > = z.lazy(() =>
    z.object({
      broadcast: z.boolean().optional(),
      read: z.boolean().optional(),
      writeWithoutResponse: z.boolean().optional(),
      write: z.boolean().optional(),
      notify: z.boolean().optional(),
      indicate: z.boolean().optional(),
      authenticatedSignedWrites: z.boolean().optional(),
      extendedProperties: z.boolean().optional(),
    }),
  );
}
export namespace Bluetooth {
  export const RequestDeviceSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Bluetooth {
  export const RequestDeviceInfoSchema: z.ZodLazy<
    z.ZodObject<{
      id: typeof Bluetooth.RequestDeviceSchema;
      name: z.ZodUnion<readonly [z.ZodString, z.ZodNull]>;
    }>
  > = z.lazy(() =>
    z.object({
      id: Bluetooth.RequestDeviceSchema,
      name: z.union([z.string(), z.null()]),
    }),
  );
}
export namespace Bluetooth {
  export const RequestDevicePromptSchema: z.ZodLazy<z.ZodString> = z.lazy(() =>
    z.string(),
  );
}
export namespace Bluetooth {
  export const ScanRecordSchema: z.ZodLazy<
    z.ZodObject<{
      name: z.ZodOptional<z.ZodString>;
      uuids: z.ZodOptional<z.ZodArray<typeof Bluetooth.BluetoothUuidSchema>>;
      appearance: z.ZodOptional<z.ZodNumber>;
      manufacturerData: z.ZodOptional<
        z.ZodArray<typeof Bluetooth.BluetoothManufacturerDataSchema>
      >;
    }>
  > = z.lazy(() =>
    z.object({
      name: z.string().optional(),
      uuids: z.array(Bluetooth.BluetoothUuidSchema).optional(),
      appearance: z.number().optional(),
      manufacturerData: z
        .array(Bluetooth.BluetoothManufacturerDataSchema)
        .optional(),
    }),
  );
}
export const BluetoothCommandSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Bluetooth.HandleRequestDevicePromptSchema,
      typeof Bluetooth.SimulateAdapterSchema,
      typeof Bluetooth.DisableSimulationSchema,
      typeof Bluetooth.SimulatePreconnectedPeripheralSchema,
      typeof Bluetooth.SimulateAdvertisementSchema,
      typeof Bluetooth.SimulateGattConnectionResponseSchema,
      typeof Bluetooth.SimulateGattDisconnectionSchema,
      typeof Bluetooth.SimulateServiceSchema,
      typeof Bluetooth.SimulateCharacteristicSchema,
      typeof Bluetooth.SimulateCharacteristicResponseSchema,
      typeof Bluetooth.SimulateDescriptorSchema,
      typeof Bluetooth.SimulateDescriptorResponseSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Bluetooth.HandleRequestDevicePromptSchema,
    Bluetooth.SimulateAdapterSchema,
    Bluetooth.DisableSimulationSchema,
    Bluetooth.SimulatePreconnectedPeripheralSchema,
    Bluetooth.SimulateAdvertisementSchema,
    Bluetooth.SimulateGattConnectionResponseSchema,
    Bluetooth.SimulateGattDisconnectionSchema,
    Bluetooth.SimulateServiceSchema,
    Bluetooth.SimulateCharacteristicSchema,
    Bluetooth.SimulateCharacteristicResponseSchema,
    Bluetooth.SimulateDescriptorSchema,
    Bluetooth.SimulateDescriptorResponseSchema,
  ]),
);
export namespace Bluetooth {
  export const HandleRequestDevicePromptSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.handleRequestDevicePrompt'>;
      params: typeof Bluetooth.HandleRequestDevicePromptParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.handleRequestDevicePrompt'),
      params: Bluetooth.HandleRequestDevicePromptParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const HandleRequestDevicePromptParametersSchema: z.ZodLazy<
    z.ZodIntersection<
      z.ZodObject<{
        context: z.ZodString;
        prompt: typeof Bluetooth.RequestDevicePromptSchema;
      }>,
      z.ZodUnion<
        readonly [
          typeof Bluetooth.HandleRequestDevicePromptAcceptParametersSchema,
          typeof Bluetooth.HandleRequestDevicePromptCancelParametersSchema,
        ]
      >
    >
  > = z.lazy(() =>
    z
      .object({
        context: z.string(),
        prompt: Bluetooth.RequestDevicePromptSchema,
      })
      .and(
        z.union([
          Bluetooth.HandleRequestDevicePromptAcceptParametersSchema,
          Bluetooth.HandleRequestDevicePromptCancelParametersSchema,
        ]),
      ),
  );
}
export namespace Bluetooth {
  export const HandleRequestDevicePromptAcceptParametersSchema: z.ZodLazy<
    z.ZodObject<{
      accept: z.ZodLiteral<true>;
      device: typeof Bluetooth.RequestDeviceSchema;
    }>
  > = z.lazy(() =>
    z.object({
      accept: z.literal(true),
      device: Bluetooth.RequestDeviceSchema,
    }),
  );
}
export namespace Bluetooth {
  export const HandleRequestDevicePromptCancelParametersSchema: z.ZodLazy<
    z.ZodObject<{
      accept: z.ZodLiteral<false>;
    }>
  > = z.lazy(() =>
    z.object({
      accept: z.literal(false),
    }),
  );
}
export namespace Bluetooth {
  export const SimulateAdapterSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulateAdapter'>;
      params: typeof Bluetooth.SimulateAdapterParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulateAdapter'),
      params: Bluetooth.SimulateAdapterParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateAdapterParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      leSupported: z.ZodOptional<z.ZodBoolean>;
      state: z.ZodEnum<{
        absent: 'absent';
        'powered-off': 'powered-off';
        'powered-on': 'powered-on';
      }>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      leSupported: z.boolean().optional(),
      state: z.enum(['absent', 'powered-off', 'powered-on']),
    }),
  );
}
export namespace Bluetooth {
  export const DisableSimulationSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.disableSimulation'>;
      params: typeof Bluetooth.DisableSimulationParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.disableSimulation'),
      params: Bluetooth.DisableSimulationParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const DisableSimulationParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
    }),
  );
}
export namespace Bluetooth {
  export const SimulatePreconnectedPeripheralSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulatePreconnectedPeripheral'>;
      params: typeof Bluetooth.SimulatePreconnectedPeripheralParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulatePreconnectedPeripheral'),
      params: Bluetooth.SimulatePreconnectedPeripheralParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulatePreconnectedPeripheralParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
      name: z.ZodString;
      manufacturerData: z.ZodArray<
        typeof Bluetooth.BluetoothManufacturerDataSchema
      >;
      knownServiceUuids: z.ZodArray<typeof Bluetooth.BluetoothUuidSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
      name: z.string(),
      manufacturerData: z.array(Bluetooth.BluetoothManufacturerDataSchema),
      knownServiceUuids: z.array(Bluetooth.BluetoothUuidSchema),
    }),
  );
}
export namespace Bluetooth {
  export const SimulateAdvertisementSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulateAdvertisement'>;
      params: typeof Bluetooth.SimulateAdvertisementParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulateAdvertisement'),
      params: Bluetooth.SimulateAdvertisementParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateAdvertisementParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      scanEntry: typeof Bluetooth.SimulateAdvertisementScanEntryParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      scanEntry: Bluetooth.SimulateAdvertisementScanEntryParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateAdvertisementScanEntryParametersSchema: z.ZodLazy<
    z.ZodObject<{
      deviceAddress: z.ZodString;
      rssi: z.ZodNumber;
      scanRecord: typeof Bluetooth.ScanRecordSchema;
    }>
  > = z.lazy(() =>
    z.object({
      deviceAddress: z.string(),
      rssi: z.number(),
      scanRecord: Bluetooth.ScanRecordSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateGattConnectionResponseSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulateGattConnectionResponse'>;
      params: typeof Bluetooth.SimulateGattConnectionResponseParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulateGattConnectionResponse'),
      params: Bluetooth.SimulateGattConnectionResponseParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateGattConnectionResponseParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
      code: z.ZodNumber;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
      code: z.number().int().nonnegative(),
    }),
  );
}
export namespace Bluetooth {
  export const SimulateGattDisconnectionSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulateGattDisconnection'>;
      params: typeof Bluetooth.SimulateGattDisconnectionParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulateGattDisconnection'),
      params: Bluetooth.SimulateGattDisconnectionParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateGattDisconnectionParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
    }),
  );
}
export namespace Bluetooth {
  export const SimulateServiceSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulateService'>;
      params: typeof Bluetooth.SimulateServiceParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulateService'),
      params: Bluetooth.SimulateServiceParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateServiceParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
      uuid: typeof Bluetooth.BluetoothUuidSchema;
      type: z.ZodEnum<{add: 'add'; remove: 'remove'}>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
      uuid: Bluetooth.BluetoothUuidSchema,
      type: z.enum(['add', 'remove']),
    }),
  );
}
export namespace Bluetooth {
  export const SimulateCharacteristicSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulateCharacteristic'>;
      params: typeof Bluetooth.SimulateCharacteristicParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulateCharacteristic'),
      params: Bluetooth.SimulateCharacteristicParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateCharacteristicParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
      serviceUuid: typeof Bluetooth.BluetoothUuidSchema;
      characteristicUuid: typeof Bluetooth.BluetoothUuidSchema;
      characteristicProperties: z.ZodOptional<
        typeof Bluetooth.CharacteristicPropertiesSchema
      >;
      type: z.ZodEnum<{add: 'add'; remove: 'remove'}>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
      serviceUuid: Bluetooth.BluetoothUuidSchema,
      characteristicUuid: Bluetooth.BluetoothUuidSchema,
      characteristicProperties:
        Bluetooth.CharacteristicPropertiesSchema.optional(),
      type: z.enum(['add', 'remove']),
    }),
  );
}
export namespace Bluetooth {
  export const SimulateCharacteristicResponseSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulateCharacteristicResponse'>;
      params: typeof Bluetooth.SimulateCharacteristicResponseParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulateCharacteristicResponse'),
      params: Bluetooth.SimulateCharacteristicResponseParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateCharacteristicResponseParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
      serviceUuid: typeof Bluetooth.BluetoothUuidSchema;
      characteristicUuid: typeof Bluetooth.BluetoothUuidSchema;
      type: z.ZodEnum<{
        read: 'read';
        write: 'write';
        'subscribe-to-notifications': 'subscribe-to-notifications';
        'unsubscribe-from-notifications': 'unsubscribe-from-notifications';
      }>;
      code: z.ZodNumber;
      data: z.ZodOptional<z.ZodArray<z.ZodNumber>>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
      serviceUuid: Bluetooth.BluetoothUuidSchema,
      characteristicUuid: Bluetooth.BluetoothUuidSchema,
      type: z.enum([
        'read',
        'write',
        'subscribe-to-notifications',
        'unsubscribe-from-notifications',
      ]),
      code: z.number().int().nonnegative(),
      data: z.array(z.number().int().nonnegative()).optional(),
    }),
  );
}
export namespace Bluetooth {
  export const SimulateDescriptorSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulateDescriptor'>;
      params: typeof Bluetooth.SimulateDescriptorParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulateDescriptor'),
      params: Bluetooth.SimulateDescriptorParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateDescriptorParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
      serviceUuid: typeof Bluetooth.BluetoothUuidSchema;
      characteristicUuid: typeof Bluetooth.BluetoothUuidSchema;
      descriptorUuid: typeof Bluetooth.BluetoothUuidSchema;
      type: z.ZodEnum<{add: 'add'; remove: 'remove'}>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
      serviceUuid: Bluetooth.BluetoothUuidSchema,
      characteristicUuid: Bluetooth.BluetoothUuidSchema,
      descriptorUuid: Bluetooth.BluetoothUuidSchema,
      type: z.enum(['add', 'remove']),
    }),
  );
}
export namespace Bluetooth {
  export const SimulateDescriptorResponseSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.simulateDescriptorResponse'>;
      params: typeof Bluetooth.SimulateDescriptorResponseParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.simulateDescriptorResponse'),
      params: Bluetooth.SimulateDescriptorResponseParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const SimulateDescriptorResponseParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
      serviceUuid: typeof Bluetooth.BluetoothUuidSchema;
      characteristicUuid: typeof Bluetooth.BluetoothUuidSchema;
      descriptorUuid: typeof Bluetooth.BluetoothUuidSchema;
      type: z.ZodEnum<{read: 'read'; write: 'write'}>;
      code: z.ZodNumber;
      data: z.ZodOptional<z.ZodArray<z.ZodNumber>>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
      serviceUuid: Bluetooth.BluetoothUuidSchema,
      characteristicUuid: Bluetooth.BluetoothUuidSchema,
      descriptorUuid: Bluetooth.BluetoothUuidSchema,
      type: z.enum(['read', 'write']),
      code: z.number().int().nonnegative(),
      data: z.array(z.number().int().nonnegative()).optional(),
    }),
  );
}
export const BluetoothEventSchema: z.ZodLazy<
  z.ZodUnion<
    readonly [
      typeof Bluetooth.RequestDevicePromptUpdatedSchema,
      typeof Bluetooth.GattConnectionAttemptedSchema,
    ]
  >
> = z.lazy(() =>
  z.union([
    Bluetooth.RequestDevicePromptUpdatedSchema,
    Bluetooth.GattConnectionAttemptedSchema,
  ]),
);
export namespace Bluetooth {
  export const RequestDevicePromptUpdatedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.requestDevicePromptUpdated'>;
      params: typeof Bluetooth.RequestDevicePromptUpdatedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.requestDevicePromptUpdated'),
      params: Bluetooth.RequestDevicePromptUpdatedParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const RequestDevicePromptUpdatedParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      prompt: typeof Bluetooth.RequestDevicePromptSchema;
      devices: z.ZodArray<typeof Bluetooth.RequestDeviceInfoSchema>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      prompt: Bluetooth.RequestDevicePromptSchema,
      devices: z.array(Bluetooth.RequestDeviceInfoSchema),
    }),
  );
}
export namespace Bluetooth {
  export const GattConnectionAttemptedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.gattConnectionAttempted'>;
      params: typeof Bluetooth.GattConnectionAttemptedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.gattConnectionAttempted'),
      params: Bluetooth.GattConnectionAttemptedParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const GattConnectionAttemptedParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
    }),
  );
}
export namespace Bluetooth {
  export const CharacteristicEventGeneratedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.characteristicEventGenerated'>;
      params: typeof Bluetooth.CharacteristicEventGeneratedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.characteristicEventGenerated'),
      params: Bluetooth.CharacteristicEventGeneratedParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const CharacteristicEventGeneratedParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
      serviceUuid: typeof Bluetooth.BluetoothUuidSchema;
      characteristicUuid: typeof Bluetooth.BluetoothUuidSchema;
      type: z.ZodEnum<{
        read: 'read';
        'write-with-response': 'write-with-response';
        'write-without-response': 'write-without-response';
        'subscribe-to-notifications': 'subscribe-to-notifications';
        'unsubscribe-from-notifications': 'unsubscribe-from-notifications';
      }>;
      data: z.ZodOptional<z.ZodArray<z.ZodNumber>>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
      serviceUuid: Bluetooth.BluetoothUuidSchema,
      characteristicUuid: Bluetooth.BluetoothUuidSchema,
      type: z.enum([
        'read',
        'write-with-response',
        'write-without-response',
        'subscribe-to-notifications',
        'unsubscribe-from-notifications',
      ]),
      data: z.array(z.number().int().nonnegative()).optional(),
    }),
  );
}
export namespace Bluetooth {
  export const DescriptorEventGeneratedSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'bluetooth.descriptorEventGenerated'>;
      params: typeof Bluetooth.DescriptorEventGeneratedParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('bluetooth.descriptorEventGenerated'),
      params: Bluetooth.DescriptorEventGeneratedParametersSchema,
    }),
  );
}
export namespace Bluetooth {
  export const DescriptorEventGeneratedParametersSchema: z.ZodLazy<
    z.ZodObject<{
      context: z.ZodString;
      address: z.ZodString;
      serviceUuid: typeof Bluetooth.BluetoothUuidSchema;
      characteristicUuid: typeof Bluetooth.BluetoothUuidSchema;
      descriptorUuid: typeof Bluetooth.BluetoothUuidSchema;
      type: z.ZodEnum<{read: 'read'; write: 'write'}>;
      data: z.ZodOptional<z.ZodArray<z.ZodNumber>>;
    }>
  > = z.lazy(() =>
    z.object({
      context: z.string(),
      address: z.string(),
      serviceUuid: Bluetooth.BluetoothUuidSchema,
      characteristicUuid: Bluetooth.BluetoothUuidSchema,
      descriptorUuid: Bluetooth.BluetoothUuidSchema,
      type: z.enum(['read', 'write']),
      data: z.array(z.number().int().nonnegative()).optional(),
    }),
  );
}
